"use client";

import { ReactNode, ComponentPropsWithoutRef } from "react";
import { motion } from "framer-motion";
import { cn } from "@/util";
import { Slot } from "@radix-ui/react-slot";

type MotionButtonProps = ComponentPropsWithoutRef<typeof motion.button>;

interface ButtonProps extends Omit<MotionButtonProps, "children"> {
  asChild?: boolean;
  children: ReactNode;
}

export const Button = ({
  className,
  children,
  asChild = false,
  ...props
}: ButtonProps) => {
  const sharedClasses = cn(
    "relative px-4 first-letter:capitalize py-1 text-base md:text-lg font-medium text-white rounded-lg",
    "border border-white/10 shadow-[inset_0_0_6px_3px_rgba(255,255,255,0.15)]",
    "bg-[#5A52FF]/80 backdrop-blur-[2px]",
    "overflow-hidden isolate",
    "transition-all duration-300 ease-out",
    className
  );

  // 🟣 Option 2 applied here — motion only if asChild is false
  if (asChild) {
    return (
      <Slot className={sharedClasses}>
        <span className="relative inline-flex items-center justify-center">
          <span className="relative z-10 font-medium">{children}</span>
          <div className="absolute inset-0 rounded-lg bg-gradient-to-br from-white/0 via-black/0 to-black/50 opacity-100 pointer-events-none" />
          <motion.div
            className="absolute inset-0 rounded-lg bg-[#5A52FF]/60 pointer-events-none"
            animate={{
              opacity: [0.2, 0.4, 0.6, 0.8, 0.6, 0.4, 0.2],
            }}
            transition={{
              duration: 3,
              repeat: Infinity,
              ease: "easeInOut",
            }}
          />
        </span>
      </Slot>
    );
  }
  return (
    <motion.button
      whileHover={{
        scale: 1.03,
        boxShadow: "0 0 12px rgba(255,255,255,0.25)",
      }}
      whileTap={{ scale: 0.97 }}
      transition={{ type: "spring", stiffness: 300, damping: 20 }}
      className={sharedClasses}
      {...props}
    >
      <span className="relative z-10 font-medium">{children}</span>
      <div className="absolute inset-0 rounded-lg bg-gradient-to-br from-white/0 via-black/0 to-black/50 opacity-100 pointer-events-none" />
      <motion.div
        className="absolute inset-0 rounded-lg bg-[#5A52FF]/60 pointer-events-none"
        animate={{
          opacity: [0.2, 0.4, 0.6, 0.8, 0.6, 0.4, 0.2],
        }}
        transition={{
          duration: 3,
          repeat: Infinity,
          ease: "easeInOut",
        }}
      />
    </motion.button>
  );
};
